import type { StrapiSampleInformationRow, StrapiSurveyGridRow } from '../../api/strapiCrm';
import { bidangLabelFromSamplingPlan } from '../../api/strapiCrm';
import type { DynamicEntityRow } from '../../api/strapiDynamicEntity';
import {
  letterCountBySamplingPlanFromRows,
  pickCanonicalSamplingPlans,
} from '../../sampling/samplingPlanCanonical';

export type WorkOrderPlanChangeKind = 'new_plan' | 'extended_points';

export type WorkOrderPlanChangePlanSummary = {
  sampling_plan_document_id: string;
  bidang_pengujian_label: string;
  kind: WorkOrderPlanChangeKind;
  pending_point_count: number;
  point_labels: string[];
};

export type WorkOrderPlanChangeSummary = {
  pending_rows: StrapiSurveyGridRow[];
  pending_row_keys: Set<string>;
  plan_summaries: WorkOrderPlanChangePlanSummary[];
  total_pending_points: number;
  new_plan_count: number;
  extended_plan_count: number;
};

export type SamplingPlanAddedPointsMeta = {
  documentId?: unknown;
  sample_point_count?: unknown;
  added_sample_point_count?: unknown;
};

function rowKey(row: StrapiSurveyGridRow): string {
  return String(row.row_key ?? '').trim();
}

function asAddedPointCount(raw: unknown): number {
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

function asSamplePointCount(raw: unknown): number {
  const n = Number(raw);
  return Number.isFinite(n) && n >= 1 ? Math.floor(n) : 1;
}

/** Titik asli quotation/WO = total titik dikurangi yang ditambah lewat modal tambah titik. */
export function originalSamplePointCount(
  samplePointCount: unknown,
  addedSamplePointCount: unknown,
): number {
  const total = asSamplePointCount(samplePointCount);
  const added = asAddedPointCount(addedSamplePointCount);
  return Math.max(0, total - added);
}

/**
 * Revisi titik tambahan sudah diteruskan PIC (`submitted`) atau disetujui SPV / manajer.
 * Banner tambah titik tidak perlu ditampilkan lagi.
 */
export function quotationClearsTambahTitikBanner(status?: string | null): boolean {
  const s = String(status ?? '').trim();
  return (
    s === 'submitted' ||
    s === 'approved' ||
    s === 'need_manajer_approval' ||
    s === 'manajer_approved' ||
    s === 'customer_approved'
  );
}

function addedPointMetaByPlanId(
  plans: SamplingPlanAddedPointsMeta[],
): Map<string, { originalCount: number; addedCount: number }> {
  const map = new Map<string, { originalCount: number; addedCount: number }>();
  for (const plan of plans) {
    const id = String(plan.documentId ?? '').trim();
    if (!id) continue;
    const addedCount = asAddedPointCount(plan.added_sample_point_count);
    if (addedCount < 1) continue;
    map.set(id, {
      addedCount,
      originalCount: originalSamplePointCount(plan.sample_point_count, addedCount),
    });
  }
  return map;
}

/** Gabungkan label bidang pengujian terbaru dari sampling_plans work order ke baris survey. */
export function mergeSurveyRowsWithLatestPlans(
  rows: StrapiSurveyGridRow[],
  plans: DynamicEntityRow[],
  jenisIndexPlans: DynamicEntityRow[] = plans,
  sampleLines: StrapiSampleInformationRow[] = [],
): StrapiSurveyGridRow[] {
  if (!plans.length) return rows;
  const planById = new Map<string, DynamicEntityRow>();
  for (const plan of plans) {
    const id = String(plan.documentId ?? '').trim();
    if (id) planById.set(id, plan);
  }
  return rows.map((row) => {
    const planId = String(row.sampling_plan_document_id ?? '').trim();
    const plan = planId ? planById.get(planId) : undefined;
    if (!plan) return row;
    const freshLabel = bidangLabelFromSamplingPlan(plan, jenisIndexPlans, sampleLines).trim();
    if (!freshLabel || freshLabel === row.bidang_pengujian_label) return row;
    return { ...row, bidang_pengujian_label: freshLabel };
  });
}

/** Filter baris ke sampling plan kanonik WO + label bidang terbaru. */
export function applyCanonicalPlansToSurveyRows(
  rows: StrapiSurveyGridRow[],
  plans: DynamicEntityRow[],
  sampleLines: StrapiSampleInformationRow[] = [],
): StrapiSurveyGridRow[] {
  const letterCounts = letterCountBySamplingPlanFromRows(rows);
  const canonical = pickCanonicalSamplingPlans(plans, letterCounts);
  const canonicalIds = new Set(
    canonical.map((plan) => String(plan.documentId ?? '').trim()).filter(Boolean),
  );
  const filtered = canonicalIds.size
    ? rows.filter((row) => canonicalIds.has(String(row.sampling_plan_document_id ?? '').trim()))
    : rows;
  return mergeSurveyRowsWithLatestPlans(filtered, canonical, plans, sampleLines);
}

/** Titik dari modal tambah titik yang belum punya berita acara. */
export function computeWorkOrderPlanChangeSummary(
  rows: StrapiSurveyGridRow[],
  plans: SamplingPlanAddedPointsMeta[] = [],
  quotation?: { progress_status?: string | null } | null,
): WorkOrderPlanChangeSummary | null {
  if (quotationClearsTambahTitikBanner(quotation?.progress_status)) return null;
  if (!rows.length) return null;

  const metaByPlan = addedPointMetaByPlanId(plans);
  if (!metaByPlan.size) return null;

  const pendingRows = rows.filter((row) => {
    if (row.status !== 'not_started') return false;
    const planId = String(row.sampling_plan_document_id ?? '').trim();
    const meta = metaByPlan.get(planId);
    if (!meta) return false;
    return Number(row.point_index) > meta.originalCount;
  });
  if (!pendingRows.length) return null;

  const rowsByPlan = new Map<string, StrapiSurveyGridRow[]>();
  for (const row of pendingRows) {
    const planId = String(row.sampling_plan_document_id ?? '').trim();
    if (!planId) continue;
    const list = rowsByPlan.get(planId) ?? [];
    list.push(row);
    rowsByPlan.set(planId, list);
  }

  const planSummaries: WorkOrderPlanChangePlanSummary[] = [];
  for (const [planId, planPendingRows] of rowsByPlan) {
    if (!planPendingRows.length) continue;
    const bidang =
      planPendingRows[0]?.bidang_pengujian_label?.trim() || 'Rencana sampling';
    planSummaries.push({
      sampling_plan_document_id: planId,
      bidang_pengujian_label: bidang,
      kind: 'extended_points',
      pending_point_count: planPendingRows.length,
      point_labels: planPendingRows.map((row) => row.point_label || `Titik ${row.point_index}`),
    });
  }

  planSummaries.sort((a, b) => a.bidang_pengujian_label.localeCompare(b.bidang_pengujian_label, 'id'));

  const pendingRowKeys = new Set(pendingRows.map(rowKey).filter(Boolean));

  return {
    pending_rows: pendingRows,
    pending_row_keys: pendingRowKeys,
    plan_summaries: planSummaries,
    total_pending_points: pendingRows.length,
    new_plan_count: 0,
    extended_plan_count: planSummaries.length,
  };
}

export function workOrderPlanChangeBannerMessage(summary: WorkOrderPlanChangeSummary): string {
  const detail = `${summary.extended_plan_count} rencana dengan titik tambahan (${summary.total_pending_points} titik)`;
  return `Petugas sampling menambah titik lewat tambah titik sampling. ${detail} belum memiliki berita acara. Mulai titik baru satu per satu lewat tombol play di tabel berita acara.`;
}
